import React, { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { useUID } from 'dibs-uid';
import ResponsiveImage from '../ResponsiveImage';
import classnames from 'classnames';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';

import styles from './HpRespSkinnyBannerModule.scss';

import { HpRespSkinnyBannerModule_componentModule } from './__generated__/HpRespSkinnyBannerModule_componentModule.graphql';

type BannerItems = NonNullable<NonNullable<HpRespSkinnyBannerModule_componentModule['items']>>[0];
type LinkData = NonNullable<BannerItems>['linkData'];

type Props = {
    componentModule: HpRespSkinnyBannerModule_componentModule;
    useLazyLoadImages: boolean;
};

const HpRespSkinnyBannerModule: FC<Props> = ({ componentModule, useLazyLoadImages }) => {
    const uid = `skinnyBanner-${useUID() || undefined}`;

    if (!componentModule) {
        return null;
    }

    const bannerItem = componentModule?.items?.[0];
    const imgSrc = bannerItem?.imageUrl || bannerItem?.mobileImageUrl || '';
    const imageCopy = bannerItem?.imageCopy || undefined;
    const { isFullBleedImage, imageHeight, imageWidth, mobileImageHeight, mobileImageWidth } =
        bannerItem || {};

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.isFullBleed]: isFullBleedImage,
            })}
        >
            {imageCopy && (
                <span id={uid} className={styles.imageCopy}>
                    {imageCopy}
                </span>
            )}
            <SeoLink
                dataTn="skinny-banner-link"
                linkData={bannerItem?.linkData as LinkData}
                ariaLabel={bannerItem?.cta || undefined}
                ariaDescribedBy={imageCopy ? uid : undefined}
            >
                <HpLazyImageWrapper
                    useLazyLoad={useLazyLoadImages}
                    imageHeight={imageHeight || mobileImageHeight}
                    imageWidth={imageWidth || mobileImageWidth}
                >
                    <ResponsiveImage
                        ariaHidden
                        className={classnames(styles.image, {
                            [styles.isFullBleed]: isFullBleedImage,
                        })}
                        src={imgSrc}
                        srcSetWidths={[400, 640, 960, 1280, 1440]}
                    />
                </HpLazyImageWrapper>
            </SeoLink>
        </div>
    );
};

export default createFragmentContainer(HpRespSkinnyBannerModule, {
    componentModule: graphql`
        fragment HpRespSkinnyBannerModule_componentModule on SkinnyBannerModule {
            items {
                ... on SkinnyBannerModuleItem @skip(if: $isMobile) {
                    imageUrl
                    imageHeight
                    imageWidth
                }
                ... on SkinnyBannerModuleItem @include(if: $isMobile) {
                    mobileImageUrl
                    mobileImageHeight
                    mobileImageWidth
                }
                linkData {
                    ...SeoLink_linkData
                }
                cta
                imageCopy
                isFullBleedImage
            }
        }
    `,
});
